/**
 * Master-only sync API key management, via /api/sync/keys. The keys table is
 * server-only, so this goes through the endpoint (not Supabase directly), sending
 * the caller's master session token. The plaintext key is returned exactly once,
 * on create.
 *
 * @module services/syncApiKeyService
 */

import { getSupabaseClient } from "@/config/supabase";
import { logError } from "@/services/logger";

export interface SyncApiKey {
  id: string;
  label: string | null;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

export interface ServiceResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

const authedFetch = async (init: RequestInit): Promise<Response> => {
  const { data } = await getSupabaseClient().auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("Not signed in");
  return fetch("/api/sync/keys", {
    ...init,
    headers: { ...(init.headers ?? {}), "Content-Type": "application/json", Authorization: `Bearer ${token}` },
  });
};

export const listSyncApiKeys = async (): Promise<ServiceResult<SyncApiKey[]>> => {
  try {
    const resp = await authedFetch({ method: "GET" });
    const data = (await resp.json().catch(() => null)) as { keys?: SyncApiKey[]; error?: string } | null;
    if (!resp.ok) return { success: false, error: data?.error ?? "Failed to load keys" };
    return { success: true, data: data?.keys ?? [] };
  } catch (e) {
    logError("SyncApiKey", "list failed", e);
    return { success: false, error: e instanceof Error ? e.message : "Failed to load keys" };
  }
};

/** Create a key; `data.key` is the plaintext, shown once. */
export const createSyncApiKey = async (
  label: string,
): Promise<ServiceResult<{ id: string; label: string | null; key: string }>> => {
  try {
    const resp = await authedFetch({ method: "POST", body: JSON.stringify({ label }) });
    const data = (await resp.json().catch(() => null)) as
      | { id?: string; label?: string | null; key?: string; error?: string }
      | null;
    if (!resp.ok || !data?.key || !data?.id) return { success: false, error: data?.error ?? "Failed to create key" };
    return { success: true, data: { id: data.id, label: data.label ?? null, key: data.key } };
  } catch (e) {
    logError("SyncApiKey", "create failed", e);
    return { success: false, error: e instanceof Error ? e.message : "Failed to create key" };
  }
};

export const revokeSyncApiKey = async (id: string): Promise<ServiceResult<void>> => {
  try {
    const resp = await authedFetch({ method: "DELETE", body: JSON.stringify({ id }) });
    const data = (await resp.json().catch(() => null)) as { error?: string } | null;
    if (!resp.ok) return { success: false, error: data?.error ?? "Failed to revoke key" };
    return { success: true };
  } catch (e) {
    logError("SyncApiKey", "revoke failed", e);
    return { success: false, error: e instanceof Error ? e.message : "Failed to revoke key" };
  }
};
